import { useEffect, useRef } from "react";
import { publicWsUrl } from "../lib/api";

/**
 * usePublicHub — anonymous WebSocket to `/api/ws/public`. Streams
 * landing-page broadcast events such as `stats:bump`. Auto-reconnects
 * with 1s → 15s backoff just like `useUserHub`.
 *
 * Usage:
 *   usePublicHub((evt) => {
 *     if (evt.type === "stats:bump") setTotals(evt.totals);
 *   });
 *
 * No auth required; safe to mount in components that render for
 * guests. Pass `enabled=false` to opt out (e.g. once the user
 * navigates away from the landing).
 */
export function usePublicHub(onEvent, enabled = true) {
  const wsRef = useRef(null);
  const onEventRef = useRef(onEvent);
  const backoffRef = useRef(1000);
  const closedRef = useRef(false);

  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    if (!enabled) return;
    closedRef.current = false;

    const connect = () => {
      if (closedRef.current) return;
      let ws;
      try {
        ws = new WebSocket(publicWsUrl());
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => { backoffRef.current = 1000; };
      ws.onmessage = (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload?.type === "pong" || payload?.type === "ready") return;
          onEventRef.current?.(payload);
        } catch { /* ignore */ }
      };
      ws.onerror = () => { /* close handler will reconnect */ };
      ws.onclose = () => { scheduleReconnect(); };
    };

    const scheduleReconnect = () => {
      if (closedRef.current) return;
      const delay = Math.min(15000, backoffRef.current);
      backoffRef.current = Math.min(15000, backoffRef.current * 2);
      setTimeout(connect, delay);
    };

    connect();

    const pingId = setInterval(() => {
      try {
        if (wsRef.current?.readyState === 1) {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
        }
      } catch { /* ignore */ }
    }, 25000);

    return () => {
      closedRef.current = true;
      clearInterval(pingId);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [enabled]);
}
